import { useEffect, useRef } from 'react';

export type HotkeyMap = Record<string, (e: KeyboardEvent) => void>;

/** "ctrl+k", "f10", "shift+f8", "alt+1", "?", "escape", "delete". "mod" = ctrl on Windows/Linux, ⌘ on Mac. */
export function keyName(e: KeyboardEvent): string {
  const parts: string[] = [];
  const mac = navigator.platform.toLowerCase().includes('mac');
  if ((mac ? e.metaKey : e.ctrlKey)) parts.push('mod');
  if (e.altKey) parts.push('alt');
  if (e.shiftKey && e.key.length > 1) parts.push('shift');
  let k = e.key.toLowerCase();
  if (k === ' ') k = 'space';
  if (e.altKey && /^digit\d$/i.test(e.code)) k = e.code.slice(5);
  if (e.altKey && /^Key[A-Z]$/.test(e.code)) k = e.code.slice(3).toLowerCase(); // Alt+C on a Mac types ç

  parts.push(k);
  return parts.join('+');
}

export function isTyping(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  return t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName);
}

/**
 * Register shortcuts while the component is mounted. Keys prefixed with "!" also fire while typing
 * in a field (function keys, mod+…, escape) — plain letters never do.
 */
export function useHotkeys(map: HotkeyMap, enabled = true) {
  const ref = useRef(map);
  ref.current = map;
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      const name = keyName(e);
      const typing = isTyping(e);
      const always = ref.current['!' + name];
      const normal = ref.current[name];
      const fn = always ?? (!typing ? normal : undefined);
      if (fn) {
        e.preventDefault();
        fn(e);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);
}

export const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches;
